import { useMemo, useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { SPECTRUM_COLORS, getBlockColor } from './SpectrumMaterial';

const SpectrumBoardGrid3D = ({
  rows = 20,
  columns = 10,
  cellSize = 1,
  currentPiece, // Dynamic color source
}) => {
  const halfWidth = (columns * cellSize) / 2;
  const height = rows * cellSize;
  const borderRadius = 0.3;
  const borderThickness = 0.15;
  const borderDepth = 0.3;

  // Dynamic border color based on current tetromino
  const borderColor = useMemo(() => {
    if (currentPiece?.colorClass) {
      return getBlockColor(currentPiece.colorClass);
    }
    return '#1a1a1a'; // Dark neutral gray when no piece
  }, [currentPiece?.colorClass]);

  const borderRef = useRef();

  // Animate border color smoothly
  useFrame(() => {
    if (!borderRef.current) return;

    const targetColor = new THREE.Color(borderColor);
    const currentColor = borderRef.current.material.color;

    // Smooth color transition
    currentColor.lerp(targetColor, 0.1);
    borderRef.current.material.emissive.lerp(targetColor, 0.1);
  });

  const gridLines = useMemo(() => {
    const points = [];

    // Vertical lines
    for (let i = 0; i <= columns; i++) {
      const x = i * cellSize - halfWidth;
      points.push(new THREE.Vector3(x, 0, -0.1));
      points.push(new THREE.Vector3(x, height, -0.1));
    }

    // Horizontal lines
    for (let i = 0; i <= rows; i++) {
      const y = i * cellSize;
      points.push(new THREE.Vector3(-halfWidth, y, -0.1));
      points.push(new THREE.Vector3(halfWidth, y, -0.1));
    }

    return points;
  }, [rows, columns, cellSize, halfWidth, height]);

  const geometry = useMemo(() => {
    const geo = new THREE.BufferGeometry().setFromPoints(gridLines);
    return geo;
  }, [gridLines]);

  // Create rounded rectangle border shape
  const borderShape = useMemo(() => {
    const shape = new THREE.Shape();
    const w = columns * cellSize + borderThickness * 2;
    const h = rows * cellSize + borderThickness * 2;
    const r = borderRadius;

    // Outer rounded rectangle
    shape.moveTo(-w/2 + r, -h/2);
    shape.lineTo(w/2 - r, -h/2);
    shape.quadraticCurveTo(w/2, -h/2, w/2, -h/2 + r);
    shape.lineTo(w/2, h/2 - r);
    shape.quadraticCurveTo(w/2, h/2, w/2 - r, h/2);
    shape.lineTo(-w/2 + r, h/2);
    shape.quadraticCurveTo(-w/2, h/2, -w/2, h/2 - r);
    shape.lineTo(-w/2, -h/2 + r);
    shape.quadraticCurveTo(-w/2, -h/2, -w/2 + r, -h/2);

    // Inner cutout (the board area)
    const innerW = columns * cellSize;
    const innerH = rows * cellSize;
    const hole = new THREE.Path();
    hole.moveTo(-innerW/2, -innerH/2);
    hole.lineTo(innerW/2, -innerH/2);
    hole.lineTo(innerW/2, innerH/2);
    hole.lineTo(-innerW/2, innerH/2);
    hole.lineTo(-innerW/2, -innerH/2);
    shape.holes.push(hole);

    return shape;
  }, [rows, columns, cellSize, borderRadius, borderThickness]);

  return (
    <group>
      {/* Grid lines - hidden for clean look */}
      <lineSegments geometry={geometry}>
        <lineBasicMaterial
          color={SPECTRUM_COLORS.gridLine || '#2A3A4A'}
          opacity={0}
          transparent
        />
      </lineSegments>

      {/* Back panel - fills the entire board area */}
      <mesh position={[0, rows * cellSize / 2, -0.2]} receiveShadow>
        <planeGeometry args={[columns * cellSize, rows * cellSize]} />
        <meshStandardMaterial
          color={SPECTRUM_COLORS.background}
          opacity={0.95}
          transparent
        />
      </mesh>

      {/* Border frame with dynamic color from current tetromino */}
      <mesh ref={borderRef} position={[0, height / 2, 0.05]}>
        <extrudeGeometry
          args={[
            borderShape,
            {
              depth: borderDepth,
              bevelEnabled: true,
              bevelThickness: 0.05,
              bevelSize: 0.03,
              bevelSegments: 3
            }
          ]}
        />
        <meshStandardMaterial
          color={borderColor}
          emissive={borderColor}
          emissiveIntensity={0.3}
          metalness={0.6}
          roughness={0.3}
        />
      </mesh>
    </group>
  );
};

export default SpectrumBoardGrid3D;
